// src/hooks/useQuestionnaireDimensionRiskRules.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createQuestionnaireDimensionRiskRule,
  deleteQuestionnaireDimensionRiskRule,
  getQuestionnaireDimensionRiskRule,
  listQuestionnaireDimensionRiskRuleLookups,
  listQuestionnaireDimensionRiskRules,
  setQuestionnaireDimensionRiskRuleStatus,
  updateQuestionnaireDimensionRiskRule,
} from "@/services/questionnaireDimensionRiskRules";
import type { QuestionnaireDimensionRiskRuleUpsertIn } from "@/types/questionnaireDimensionRiskRules";

export function useQuestionnaireDimensionRiskRules(params: {
  dimension_id?: string;
  is_active?: boolean;
}) {
  return useQuery({
    queryKey: ["questionnaire_dimension_risk_rules", params],
    queryFn: () => listQuestionnaireDimensionRiskRules(params),
    staleTime: 20_000,
  });
}

export function useQuestionnaireDimensionRiskRuleDetail(ruleId?: string) {
  return useQuery({
    queryKey: ["questionnaire_dimension_risk_rule_detail", ruleId],
    queryFn: () => getQuestionnaireDimensionRiskRule(ruleId as string),
    enabled: !!ruleId,
    staleTime: 20_000,
  });
}

export function useQuestionnaireDimensionRiskRuleLookups() {
  return useQuery({
    queryKey: ["questionnaire_dimension_risk_rule_lookups"],
    queryFn: () => listQuestionnaireDimensionRiskRuleLookups(),
    staleTime: 60_000,
  });
}

export function useCreateQuestionnaireDimensionRiskRule() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (body: QuestionnaireDimensionRiskRuleUpsertIn) =>
      createQuestionnaireDimensionRiskRule(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["questionnaire_dimension_risk_rules"] });
      qc.invalidateQueries({ queryKey: ["questionnaire_dimension_risk_rule_lookups"] });
    },
  });
}

export function useUpdateQuestionnaireDimensionRiskRule() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      ruleId,
      body,
    }: {
      ruleId: string;
      body: QuestionnaireDimensionRiskRuleUpsertIn;
    }) => updateQuestionnaireDimensionRiskRule(ruleId, body),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["questionnaire_dimension_risk_rules"] });
      qc.invalidateQueries({
        queryKey: ["questionnaire_dimension_risk_rule_detail", variables.ruleId],
      });
    },
  });
}

export function useSetQuestionnaireDimensionRiskRuleStatus() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      ruleId,
      is_active,
    }: {
      ruleId: string;
      is_active: boolean;
    }) => setQuestionnaireDimensionRiskRuleStatus(ruleId, is_active),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["questionnaire_dimension_risk_rules"] });
      qc.invalidateQueries({
        queryKey: ["questionnaire_dimension_risk_rule_detail", variables.ruleId],
      });
    },
  });
}

export function useDeleteQuestionnaireDimensionRiskRule() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (ruleId: string) => deleteQuestionnaireDimensionRiskRule(ruleId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["questionnaire_dimension_risk_rules"] });
    },
  });
}
